/**
 * Block Examples Library v2 — shared contract, validation and contract fit.
 *
 * Library examples deliberately have two representations:
 * - `blueprint` is the compact, semantic input used by adaptation agents;
 * - `previewScreens` are full strategy StepNodes used only for exact preview.
 *
 * Pure module: no Firebase, React or Node-only crypto dependencies.
 */

import type {
  AiScreenMeta,
  BlockContract,
  BlockDataUsage,
  SimplifiedFunnelVariable,
  SimplifiedV2Block,
  SimplifiedV2StepNode,
  StrategyBlockInstruction,
  StrategyStepNode,
} from '@segmently/schema-models/simplified';
import type { AgentUpdateBlockRequest } from './api.types';

// ============================================================================
// Limits and versions
// ============================================================================

export const BLOCK_LIBRARY_SCHEMA_VERSION = 2 as const;

export const BLOCK_LIBRARY_LIMITS = {
  /** Parity with BLOCK_SUBTYPE_LIMIT. */
  maxExamplesPerProject: 30,
  maxNameLength: 160,
  maxDescriptionLength: 1_000,
  maxTags: 8,
  maxTagLength: 40,
  maxAssetPatternLength: 1_200,
  maxAssetNegativePatternLength: 800,
  maxAssetStyleLength: 160,
  maxAssetAspectRatioLength: 32,
  /** Firestore 1 MiB doc ceiling minus headroom for showcase/audit metadata. */
  maxPayloadBytes: 900_000,
} as const;

const CUSTOM_SUBTYPE_SLUG = /^[a-z0-9][a-z0-9_-]*$/;
const SOURCE_CHECKSUM = /^fnv1a:[a-f0-9]{8}$/;

// ============================================================================
// Export envelope (generic strategy export only — never a library record)
// ============================================================================

/**
 * Structural mirror of strategy-blocks' portable export. Kept for the generic
 * export/import surface. Block-library v2 records must not store this shape.
 */
export interface BlockExportEnvelopeJson {
  __segmently_block_export__: true;
  version: string;
  type: 'update-block';
  payload: AgentUpdateBlockRequest;
  metadata: {
    exportedAt: string;
    projectId: string;
    strategyId: string;
    blockId: string;
    blockName?: string;
    assetWorkspace?: 'regeneration' | 'portable';
  };
}

// ============================================================================
// Library v2 document types
// ============================================================================

export interface BlockLibrarySourceRef {
  strategyId: string;
  blockId: string;
  blockEntityId: string;
}

export interface BlockLibraryContractSummary {
  inputs: number;
  outputs: number;
}

export interface BlockLibraryScopeMetadata {
  blockType: string;
  customSubtype?: string;
}

export interface BlockLibraryScreenDataSemantics {
  screenId: string;
  dataMapping: NonNullable<AiScreenMeta['dataMapping']>;
  variableRealizations: NonNullable<AiScreenMeta['variableRealizations']>;
}

export interface BlockLibraryDataSemantics {
  blockDataUsage?: BlockDataUsage;
  screens: BlockLibraryScreenDataSemantics[];
}

/**
 * A selected source asset experiment retained as a creative reference.
 * Provider/model/reference payloads and generated results are intentionally
 * excluded; target prompt packages are regenerated after adaptation.
 */
export interface BlockLibraryAssetExample {
  screenId: string;
  slotKey: string;
  experimentId?: string;
  promptTemplate?: string;
  negativePrompt?: string;
  style?: string;
  aspectRatio?: string;
  assetType?: 'image' | 'video';
}

export type BlockLibraryStrategicBlock = Omit<
  SimplifiedV2Block,
  'screens' | 'instructions' | 'blockContract' | 'dataUsage'
>;

export interface BlockLibraryBlueprint {
  strategicBlock: BlockLibraryStrategicBlock;
  screens: SimplifiedV2StepNode[];
  instructions: StrategyBlockInstruction[];
  contract: BlockContract;
  dataSemantics: BlockLibraryDataSemantics;
  assetExamples: BlockLibraryAssetExample[];
  /** Block-scoped variable closure used by fit/adaptation, never the full strategy map. */
  strategyVariables: Record<string, SimplifiedFunnelVariable>;
}

/** Client/CLI write payload — everything except server-stamped fields. */
export interface BlockLibraryExampleInput {
  schemaVersion: typeof BLOCK_LIBRARY_SCHEMA_VERSION;
  name: string;
  description?: string;
  tags?: string[];
  scopeMetadata: BlockLibraryScopeMetadata;
  /** Denormalized UI metadata; validator proves it matches the blueprint. */
  screensCount: number;
  contractSummary: BlockLibraryContractSummary;
  blueprint: BlockLibraryBlueprint;
  /** Full renderable nodes. No prompt compiler is allowed to read this field. */
  previewScreens: StrategyStepNode[];
  sourceRef: BlockLibrarySourceRef;
  sourceChecksum: string;
  /** Set when imported from systemBlockLibrary (provenance + Imported badge). */
  systemExampleId?: string;
  enabled: boolean;
}

export interface BlockLibraryExampleDoc extends BlockLibraryExampleInput {
  id: string;
  createdBy: string;
  createdAt: Date;
  updatedBy?: string;
  updatedAt?: Date;
}

// ============================================================================
// Target-strategy adaptation lifecycle
// ============================================================================

/** Evidence references are identities only; Builder resolves every value. */
export interface LibraryEvidenceRef {
  kind: 'insight' | 'review' | 'rating' | 'studyCase' | 'bigNumber' | 'task' | 'audience';
  id: string;
}

/**
 * One evidence-selection contract is persisted on the staged block and reused
 * by browser, CLI and the backend task. No caller-provided evidence values are
 * accepted by the product/runtime path.
 */
export type LibraryEvidenceSelection =
  | { mode: 'strategy-default' }
  | { mode: 'selected-only'; refs: LibraryEvidenceRef[] }
  | { mode: 'no-evidence' };

export type LibraryAdaptationMode = 'staged' | 'immediate' | 'as-is';
export type LibraryAdaptationStatus = 'staged' | 'adapting' | 'adapted' | 'failed' | 'stale';

export type LibraryVariableResolution =
  | { action: 'bind'; targetVariableId: string }
  | { action: 'create' }
  | { action: 'drop' };

export interface LibraryVariableConflictSummary {
  sourceVariableId: string;
  targetVariableId?: string;
  reason: string;
  recommendedResolution: 'bind' | 'create';
}

/**
 * D-LIVE2-6: the whole-strategy task-router precondition the library save
 * transaction enforces, projected BEFORE the provider call. The fork block's
 * goal-selection screen belongs to the strategy, not to the adapted draft, so an
 * unsatisfiable router is knowable without spending a call.
 */
export interface LibraryAdaptationTaskRouterPreflight {
  satisfiable: boolean;
  /** Entity id of the block whose edges route on the task-router variable. */
  forkBlockId?: string;
  forkBlockName?: string;
  /** Tasks the fork routes that no goal option can be mapped to. */
  unmappedTaskIds?: string[];
  reason?: 'no_goal_selection_screen' | 'unmapped_tasks';
}

export interface StructuredLibraryAdaptationError {
  code: string;
  message: string;
  retryable: boolean;
  field?: string;
  details?: Record<string, unknown>;
}

export interface LibraryAdaptationResolvedEvidenceSummary {
  requestedMode: LibraryEvidenceSelection['mode'];
  mode: LibraryEvidenceSelection['mode'];
  selectedCount: number;
  resolvedCount: number;
  /** Safe inspector projection; evidence values never leave Builder preflight. */
  items?: Array<LibraryEvidenceRef & { label: string }>;
  fallbackReason?: 'strategy-default-empty';
  blockingReason?: string;
}

/**
 * Additive metadata stored at `block.aiMeta.libraryAdaptation`. A staged block
 * intentionally has no screen documents until a successful adaptation save.
 */
export interface LibraryAdaptationState {
  schemaVersion: 1;
  mode: LibraryAdaptationMode;
  status: LibraryAdaptationStatus;
  source: {
    scope: 'project' | 'system';
    exampleId: string;
    checksum: string;
  };
  evidence: LibraryEvidenceSelection;
  /** Explicit operator choices for every incompatible source variable. */
  variableResolutions?: Record<string, LibraryVariableResolution>;
  resolvedEvidence?: LibraryAdaptationResolvedEvidenceSummary;
  contextFingerprint?: string;
  /** Canonical hash binding an idempotency key to the reviewed request semantics. */
  requestFingerprint?: string;
  idempotencyKey?: string;
  taskId?: string;
  runtimePacketChecksum?: string;
  exactInputHash?: string;
  error?: StructuredLibraryAdaptationError;
  stagedAt: string;
  updatedAt: string;
  adaptedAt?: string;
}

// ============================================================================
// Canonicalization, size and checksum
// ============================================================================

/** Stable JSON for deterministic browser/server checksums. */
export function canonicalizeBlockLibraryValue(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalizeBlockLibraryValue(item)).join(',')}]`;
  }
  if (isRecord(value)) {
    const entries = Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right));
    return `{${entries.map(([key, entry]) => (
      `${JSON.stringify(key)}:${canonicalizeBlockLibraryValue(entry)}`
    )).join(',')}}`;
  }
  if (typeof value === 'number' && !Number.isFinite(value)) return 'null';
  return JSON.stringify(value) ?? 'null';
}

function fnv1aHex(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function computeBlockLibrarySourceChecksum(input: Pick<
  BlockLibraryExampleInput,
  'schemaVersion' | 'scopeMetadata' | 'blueprint' | 'previewScreens' | 'sourceRef'
>): string {
  return `fnv1a:${fnv1aHex(canonicalizeBlockLibraryValue({
    schemaVersion: input.schemaVersion,
    sourceRef: input.sourceRef,
    scopeMetadata: input.scopeMetadata,
    blueprint: input.blueprint,
    previewScreens: input.previewScreens,
  }))}`;
}

/** UTF-8 byte length of a JSON-compatible value. */
export function estimateBlockLibraryValueBytes(value: unknown): number {
  const json = JSON.stringify(value);
  if (typeof json !== 'string') return 0;
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length;
  return Buffer.byteLength(json, 'utf8');
}

export function estimateBlockLibraryPayloadBytes(input: {
  blueprint: unknown;
  previewScreens: unknown;
}): number {
  return estimateBlockLibraryValueBytes({
    blueprint: input.blueprint,
    previewScreens: input.previewScreens,
  });
}

// ============================================================================
// Validation
// ============================================================================

export type BlockLibraryValidationResult =
  | { ok: true; value: BlockLibraryExampleInput }
  | { ok: false; errors: string[] };

/**
 * Validate and normalize one v2 write. Collects all errors so browser and CLI
 * callers receive the same actionable diagnostics. There is deliberately no
 * v1 envelope parser or migration path.
 */
export function validateBlockLibraryExampleWrite(input: unknown): BlockLibraryValidationResult {
  const errors: string[] = [];
  if (!isRecord(input)) {
    return { ok: false, errors: ['body: example body must be a JSON object.'] };
  }

  if (input.schemaVersion !== BLOCK_LIBRARY_SCHEMA_VERSION) {
    errors.push(`schemaVersion: schemaVersion must be ${BLOCK_LIBRARY_SCHEMA_VERSION}; v1 envelopes are unsupported.`);
  }
  if ('envelope' in input) {
    errors.push('envelope: v1 library envelopes are unsupported; provide blueprint and previewScreens.');
  }

  const name = typeof input.name === 'string' ? input.name.trim() : '';
  if (!name) errors.push('name: name is required.');
  else if (name.length > BLOCK_LIBRARY_LIMITS.maxNameLength) {
    errors.push(`name: name must be at most ${BLOCK_LIBRARY_LIMITS.maxNameLength} characters.`);
  }

  let description: string | undefined;
  if (input.description !== undefined && input.description !== null) {
    if (typeof input.description !== 'string') errors.push('description: description must be a string.');
    else {
      description = input.description.trim();
      if (description.length > BLOCK_LIBRARY_LIMITS.maxDescriptionLength) {
        errors.push(`description: description must be at most ${BLOCK_LIBRARY_LIMITS.maxDescriptionLength} characters.`);
      }
    }
  }

  let tags: string[] | undefined;
  if (input.tags !== undefined && input.tags !== null) {
    if (!Array.isArray(input.tags)) errors.push('tags: tags must be an array of strings.');
    else if (input.tags.length > BLOCK_LIBRARY_LIMITS.maxTags) {
      errors.push(`tags: at most ${BLOCK_LIBRARY_LIMITS.maxTags} tags are allowed.`);
    } else {
      tags = [];
      input.tags.forEach((tag, index) => {
        if (typeof tag !== 'string' || !tag.trim()) {
          errors.push(`tags: tags[${index}] must be a non-empty string.`);
          return;
        }
        const normalized = tag.trim();
        if (normalized.length > BLOCK_LIBRARY_LIMITS.maxTagLength) {
          errors.push(`tags: tags[${index}] must be at most ${BLOCK_LIBRARY_LIMITS.maxTagLength} characters.`);
          return;
        }
        tags!.push(normalized);
      });
    }
  }

  const scopeMetadata = validateScopeMetadata(input.scopeMetadata, errors);
  const blueprint = validateBlueprint(input.blueprint, errors);
  const previewScreens = validatePreviewScreens(input.previewScreens, errors);

  const screensCount = input.screensCount;
  if (typeof screensCount !== 'number' || !Number.isInteger(screensCount) || screensCount < 1) {
    errors.push('screensCount: screensCount must be a positive integer.');
  }

  let contractSummary: BlockLibraryContractSummary | undefined;
  if (isRecord(input.contractSummary)
    && isNonNegativeInteger(input.contractSummary.inputs)
    && isNonNegativeInteger(input.contractSummary.outputs)) {
    contractSummary = {
      inputs: input.contractSummary.inputs as number,
      outputs: input.contractSummary.outputs as number,
    };
  } else {
    errors.push('contractSummary: contractSummary must be non-negative integer inputs/outputs.');
  }

  if (blueprint && previewScreens) {
    validateRepresentationParity(blueprint, previewScreens, errors);
    validateAssetExampleSlots(blueprint, previewScreens, errors);
    if (screensCount !== blueprint.screens.length) {
      errors.push('screensCount: screensCount must equal blueprint.screens.length.');
    }
    const contractInputs = blueprint.contract.inputVariables?.length ?? 0;
    const contractOutputs = blueprint.contract.outputAssignment?.length ?? 0;
    if (contractSummary
      && (contractSummary.inputs !== contractInputs || contractSummary.outputs !== contractOutputs)) {
      errors.push('contractSummary: contractSummary must match blueprint.contract.');
    }
    if (scopeMetadata) {
      if (String(blueprint.strategicBlock.blockType) !== scopeMetadata.blockType) {
        errors.push('scopeMetadata.blockType: must match blueprint.strategicBlock.blockType.');
      }
      if ((blueprint.strategicBlock.customSubtype ?? undefined) !== scopeMetadata.customSubtype) {
        errors.push('scopeMetadata.customSubtype: must match blueprint.strategicBlock.customSubtype.');
      }
    }
    const forbidden = collectForbiddenBlueprintPaths(blueprint);
    if (forbidden.length > 0) {
      errors.push(`blueprint_forbidden_fields: ${forbidden.join(', ')} must not be stored in the adaptation blueprint.`);
    }
    const bytes = estimateBlockLibraryPayloadBytes({ blueprint, previewScreens });
    if (bytes > BLOCK_LIBRARY_LIMITS.maxPayloadBytes) {
      errors.push(
        `payload_too_large: blueprint + previewScreens are ${bytes} bytes — the limit is `
        + `${BLOCK_LIBRARY_LIMITS.maxPayloadBytes} bytes. Reduce preview/editor payload before saving.`,
      );
    }
  }

  const sourceRef = validateSourceRef(input.sourceRef, errors);
  const sourceChecksum = typeof input.sourceChecksum === 'string' ? input.sourceChecksum.trim() : '';
  if (!SOURCE_CHECKSUM.test(sourceChecksum)) {
    errors.push('sourceChecksum: sourceChecksum must be a deterministic fnv1a checksum.');
  } else if (scopeMetadata && blueprint && previewScreens && sourceRef) {
    const expectedChecksum = computeBlockLibrarySourceChecksum({
      schemaVersion: BLOCK_LIBRARY_SCHEMA_VERSION,
      scopeMetadata,
      blueprint,
      previewScreens,
      sourceRef,
    });
    if (sourceChecksum !== expectedChecksum) {
      errors.push(`source_checksum_mismatch: expected ${expectedChecksum}.`);
    }
  }

  let systemExampleId: string | undefined;
  if (input.systemExampleId !== undefined && input.systemExampleId !== null) {
    if (!isNonEmptyString(input.systemExampleId)) {
      errors.push('systemExampleId: systemExampleId must be a non-empty string.');
    } else systemExampleId = input.systemExampleId;
  }

  if (typeof input.enabled !== 'boolean') errors.push('enabled: enabled must be a boolean.');
  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    value: {
      schemaVersion: BLOCK_LIBRARY_SCHEMA_VERSION,
      name,
      ...(description ? { description } : {}),
      ...(tags !== undefined ? { tags } : {}),
      scopeMetadata: scopeMetadata!,
      screensCount: screensCount as number,
      contractSummary: contractSummary!,
      blueprint: blueprint!,
      previewScreens: previewScreens!,
      sourceRef: sourceRef!,
      sourceChecksum,
      ...(systemExampleId ? { systemExampleId } : {}),
      enabled: input.enabled as boolean,
    },
  };
}

export interface LibraryExampleRenderability {
  renderable: boolean;
  reason?: string;
}

/** UI-side preview viability. Accepts Firestore JSON-string storage forms. */
export function assessLibraryExampleRenderability(doc: {
  schemaVersion?: unknown;
  blueprint?: unknown;
  previewScreens?: unknown;
}): LibraryExampleRenderability {
  if (doc.schemaVersion !== BLOCK_LIBRARY_SCHEMA_VERSION) {
    return { renderable: false, reason: 'The stored example is not a v2 library record.' };
  }
  const blueprint = parseStoredJson(doc.blueprint);
  const previewScreens = parseStoredJson(doc.previewScreens);
  if (!isRecord(blueprint) || !Array.isArray(blueprint.screens)) {
    return { renderable: false, reason: 'The stored blueprint is invalid.' };
  }
  if (!Array.isArray(previewScreens) || previewScreens.length === 0) {
    return { renderable: false, reason: 'The example carries no preview screens.' };
  }
  const previewIds = previewScreens.map((screen) => isRecord(screen) ? String(screen.id ?? '') : '');
  const blueprintIds = blueprint.screens.map((screen) => isRecord(screen) ? String(screen.id ?? '') : '');
  if (canonicalizeBlockLibraryValue(previewIds) !== canonicalizeBlockLibraryValue(blueprintIds)) {
    return { renderable: false, reason: 'Blueprint and preview screen ids do not match.' };
  }
  const firstScreenId = isRecord(blueprint.strategicBlock)
    ? String(blueprint.strategicBlock.firstScreenId ?? '')
    : '';
  if (!firstScreenId || !blueprintIds.includes(firstScreenId)) {
    return { renderable: false, reason: 'The first screen is missing from the blueprint.' };
  }
  return { renderable: true };
}

export function isBlockExportEnvelope(value: unknown): value is BlockExportEnvelopeJson {
  return isRecord(value)
    && value.__segmently_block_export__ === true
    && value.type === 'update-block'
    && isRecord(value.payload)
    && isRecord(value.metadata);
}

// ============================================================================
// Contract fit (pure, shared client/server)
// ============================================================================

export interface ContractFitCandidate {
  varId: string;
  valueCompatible: boolean;
}

export interface ContractFitEntry {
  exampleVarId: string;
  kind: 'input' | 'output';
  resolution: 'bind' | 'create' | 'needs-adapt';
  targetVarId?: string;
  candidates?: ContractFitCandidate[];
  referencedValues?: string[];
}

/** Compute variable fit from the semantic blueprint only. */
export function computeContractFit(
  example: { blueprint: BlockLibraryBlueprint },
  strategyVariables: Record<string, unknown>,
): ContractFitEntry[] {
  const contract = example.blueprint.contract ?? {};
  const inputs = asVariableArray(contract.inputVariables);
  const outputs = asVariableArray(contract.outputAssignment);
  const referencedByVar = collectReferencedValues(example.blueprint);

  return [
    ...inputs.map((variable) => fitOne(variable, 'input', strategyVariables, referencedByVar)),
    ...outputs.map((variable) => fitOne(variable, 'output', strategyVariables, referencedByVar)),
  ];
}

function fitOne(
  exampleVar: Record<string, unknown>,
  kind: 'input' | 'output',
  strategyVariables: Record<string, unknown>,
  referencedByVar: Map<string, Set<string>>,
): ContractFitEntry {
  const exampleVarId = String(exampleVar.id ?? '');
  const exampleType = String(exampleVar.type ?? '');
  const referencedValues = Array.from(referencedByVar.get(exampleVarId) ?? []).sort();
  const entry: ContractFitEntry = { exampleVarId, kind, resolution: 'create' };
  if (referencedValues.length > 0) entry.referencedValues = referencedValues;

  const exact = strategyVariables[exampleVarId];
  if (isRecord(exact)) {
    const typeMatches = String(exact.type ?? '') === exampleType;
    const valueCompatible = typeMatches && valuesCoveredByOptions(referencedValues, exampleType, exact);
    if (typeMatches && valueCompatible) {
      entry.resolution = 'bind';
      entry.targetVarId = exampleVarId;
      return entry;
    }
    entry.resolution = 'needs-adapt';
    entry.candidates = [{ varId: exampleVarId, valueCompatible: Boolean(valueCompatible) }];
    return entry;
  }

  const candidates: ContractFitCandidate[] = [];
  for (const [varId, definition] of Object.entries(strategyVariables)) {
    if (!isRecord(definition) || String(definition.type ?? '') !== exampleType) continue;
    candidates.push({
      varId,
      valueCompatible: valuesCoveredByOptions(referencedValues, exampleType, definition),
    });
  }
  if (candidates.length > 0) entry.candidates = candidates;
  return entry;
}

const ENUM_TYPES = new Set(['enum', 'enum[]']);

function valuesCoveredByOptions(
  referencedValues: string[],
  type: string,
  targetVariable: Record<string, unknown>,
): boolean {
  if (!ENUM_TYPES.has(type) || referencedValues.length === 0) return true;
  const optionIds = new Set(
    (Array.isArray(targetVariable.options) ? targetVariable.options : [])
      .map((option) => (isRecord(option) ? String(option.id ?? option.value ?? '') : String(option)))
      .filter(Boolean),
  );
  return referencedValues.every((value) => optionIds.has(value));
}

function collectReferencedValues(blueprint: BlockLibraryBlueprint): Map<string, Set<string>> {
  const referenced = new Map<string, Set<string>>();
  const add = (varId: string, rawValue: unknown) => {
    if (!varId || typeof rawValue !== 'string') return;
    for (const piece of rawValue.split(',')) {
      const value = piece.trim();
      if (!value) continue;
      const bucket = referenced.get(varId) ?? new Set<string>();
      bucket.add(value);
      referenced.set(varId, bucket);
    }
  };

  walkObject(blueprint.screens, (obj) => {
    if (!Array.isArray(obj.conditions)) return;
    for (const condition of obj.conditions) {
      if (!isRecord(condition) || typeof condition.key !== 'string') continue;
      add(condition.key, condition.value);
    }
  });

  for (const [varId, definition] of Object.entries(blueprint.strategyVariables ?? {})) {
    if (!isRecord(definition) || !isRecord(definition.screenBindings)) continue;
    for (const bindings of Object.values(definition.screenBindings)) {
      if (!isRecord(bindings)) continue;
      for (const optionValue of Object.values(bindings)) add(varId, optionValue);
    }
  }
  return referenced;
}

// ============================================================================
// Validation internals
// ============================================================================

function validateScopeMetadata(value: unknown, errors: string[]): BlockLibraryScopeMetadata | undefined {
  if (!isRecord(value) || !isNonEmptyString(value.blockType)) {
    errors.push('scopeMetadata: scopeMetadata.blockType is required.');
    return undefined;
  }
  const blockType = value.blockType.trim();
  let customSubtype: string | undefined;
  if (value.customSubtype !== undefined && value.customSubtype !== null) {
    if (typeof value.customSubtype !== 'string'
      || value.customSubtype !== value.customSubtype.trim()
      || !CUSTOM_SUBTYPE_SLUG.test(value.customSubtype)) {
      errors.push('scopeMetadata.customSubtype: must be a normalized lowercase slug.');
    } else if (blockType !== 'custom') {
      errors.push('scopeMetadata.customSubtype: can only be set when blockType is custom.');
    } else customSubtype = value.customSubtype;
  }
  return { blockType, ...(customSubtype ? { customSubtype } : {}) };
}

function validateBlueprint(value: unknown, errors: string[]): BlockLibraryBlueprint | undefined {
  if (!isRecord(value)) {
    errors.push('blueprint: blueprint must be an object.');
    return undefined;
  }
  if (!isRecord(value.strategicBlock)) errors.push('blueprint.strategicBlock: is required.');
  if (!Array.isArray(value.screens) || value.screens.length === 0) {
    errors.push('blueprint.screens: at least one simplified screen is required.');
  }
  if (!Array.isArray(value.instructions)) errors.push('blueprint.instructions: must be an array.');
  if (!isRecord(value.contract)) errors.push('blueprint.contract: must be an object.');
  if (!isRecord(value.dataSemantics) || !Array.isArray(value.dataSemantics.screens)) {
    errors.push('blueprint.dataSemantics: must include a screens array.');
  }
  if (!Array.isArray(value.assetExamples)) errors.push('blueprint.assetExamples: must be an array.');
  if (!isRecord(value.strategyVariables)) errors.push('blueprint.strategyVariables: must be an object.');
  if (isRecord(value.strategicBlock)) {
    const sourceOnlyFields = ['taskId', 'order', 'isFirstBlock', 'action'];
    const present = sourceOnlyFields.filter((field) => Object.prototype.hasOwnProperty.call(
      value.strategicBlock as Record<string, unknown>,
      field,
    ));
    if (present.length > 0) {
      errors.push(
        `blueprint.strategicBlock: source-only fields are forbidden (${present.join(', ')}); target placement is resolved from the target strategy.`,
      );
    }
  }
  if (errors.some((error) => error.startsWith('blueprint.'))) return undefined;
  const blueprint = value as unknown as BlockLibraryBlueprint;
  validateBlueprintSemanticReferences(blueprint, errors);
  return blueprint;
}

function validateBlueprintSemanticReferences(
  blueprint: BlockLibraryBlueprint,
  errors: string[],
): void {
  const screenIds = blueprint.screens.map((screen) => String(screen?.id ?? ''));
  const knownScreenIds = new Set(screenIds.filter(Boolean));
  const semanticScreens = blueprint.dataSemantics.screens;
  const semanticIds: string[] = [];

  blueprint.screens.forEach((screen, index) => {
    const screenErrors = validateBlockLibrarySimplifiedScreen(screen);
    for (const error of screenErrors) {
      errors.push(`blueprint.screens[${index}]${error.path ? `.${error.path}` : ''}: ${error.message}`);
    }
  });

  semanticScreens.forEach((entry, index) => {
    if (!isRecord(entry) || !isNonEmptyString(entry.screenId)) {
      errors.push(
        `blueprint.dataSemantics.screens[${index}].screenId: must identify a blueprint screen.`,
      );
      return;
    }
    semanticIds.push(entry.screenId);
    if (!knownScreenIds.has(entry.screenId)) {
      errors.push(
        `blueprint.dataSemantics.screens[${index}].screenId: unknown screen ${entry.screenId}.`,
      );
    }
    if (!Array.isArray(entry.dataMapping)) {
      errors.push(`blueprint.dataSemantics.screens[${index}].dataMapping: must be an array.`);
    }
    if (!Array.isArray(entry.variableRealizations)) {
      errors.push(
        `blueprint.dataSemantics.screens[${index}].variableRealizations: must be an array.`,
      );
    }
    const screen = blueprint.screens.find((candidate) => candidate.id === entry.screenId);
    if (screen && Array.isArray(entry.dataMapping)
      && canonicalizeBlockLibraryValue(entry.dataMapping)
        !== canonicalizeBlockLibraryValue(screen.aiMeta?.dataMapping ?? [])) {
      errors.push(
        `blueprint.dataSemantics.screens[${index}].dataMapping: must exactly match screen ${entry.screenId} aiMeta.dataMapping.`,
      );
    }
    if (screen && Array.isArray(entry.variableRealizations)
      && canonicalizeBlockLibraryValue(entry.variableRealizations)
        !== canonicalizeBlockLibraryValue(screen.aiMeta?.variableRealizations ?? [])) {
      errors.push(
        `blueprint.dataSemantics.screens[${index}].variableRealizations: must exactly match screen ${entry.screenId} aiMeta.variableRealizations.`,
      );
    }
  });

  const semanticDuplicates = semanticIds.filter((id, index) => semanticIds.indexOf(id) !== index);
  if (semanticDuplicates.length > 0) {
    errors.push(
      `blueprint.dataSemantics.screens: duplicate screen ids ${Array.from(new Set(semanticDuplicates)).join(', ')}.`,
    );
  }
  if (canonicalizeBlockLibraryValue(semanticIds) !== canonicalizeBlockLibraryValue(screenIds)) {
    errors.push(
      'blueprint.dataSemantics.screens: must cover every blueprint screen exactly once in screen order.',
    );
  }

  const assetKeys = new Set<string>();
  blueprint.assetExamples.forEach((example, index) => {
    if (!isRecord(example)) {
      errors.push(`blueprint.assetExamples[${index}]: must be an object.`);
      return;
    }
    if (!isNonEmptyString(example.screenId) || !knownScreenIds.has(example.screenId)) {
      errors.push(
        `blueprint.assetExamples[${index}].screenId: must identify a blueprint screen.`,
      );
    }
    if (!isNonEmptyString(example.slotKey)) {
      errors.push(`blueprint.assetExamples[${index}].slotKey: must be non-empty.`);
    }
    const boundedStrings: Array<[keyof BlockLibraryAssetExample, number]> = [
      ['promptTemplate', BLOCK_LIBRARY_LIMITS.maxAssetPatternLength],
      ['negativePrompt', BLOCK_LIBRARY_LIMITS.maxAssetNegativePatternLength],
      ['style', BLOCK_LIBRARY_LIMITS.maxAssetStyleLength],
      ['aspectRatio', BLOCK_LIBRARY_LIMITS.maxAssetAspectRatioLength],
    ];
    for (const [field, limit] of boundedStrings) {
      const candidate = example[field];
      if (candidate !== undefined
        && (typeof candidate !== 'string' || !candidate.trim() || candidate.length > limit)) {
        errors.push(
          `blueprint.assetExamples[${index}].${field}: must be a non-empty string of at most ${limit} characters.`,
        );
      }
    }
    if (example.assetType !== undefined && example.assetType !== 'image' && example.assetType !== 'video') {
      errors.push(`blueprint.assetExamples[${index}].assetType: must be image or video.`);
    }
    if (isNonEmptyString(example.screenId) && isNonEmptyString(example.slotKey)) {
      const key = `${example.screenId}:${example.slotKey}`;
      if (assetKeys.has(key)) {
        errors.push(`blueprint.assetExamples: duplicate screen/slot reference ${key}.`);
      }
      assetKeys.add(key);
    }
  });
}

export interface BlockLibrarySimplifiedScreenValidationIssue {
  path: string;
  message: string;
}

const BLOCK_LIBRARY_SCREEN_REQUIRED_FIELDS: Record<
  string,
  Array<{ path: string; kind: 'string' | 'array' | 'object' }>
> = {
  HeroContent: [{ path: 'content.copy.title', kind: 'string' }],
  Carousel: [
    { path: 'content.carousel.items', kind: 'array' },
    { path: 'content.carousel.timer', kind: 'object' },
  ],
  ListMultiPick: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.options.items', kind: 'array' },
  ],
  ListSinglePick: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.options.items', kind: 'array' },
  ],
  GridMultiPick: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.options.items', kind: 'array' },
  ],
  GridSinglePick: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.options.items', kind: 'array' },
  ],
  HeroList: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.options.items', kind: 'array' },
  ],
  TextInput: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.textField', kind: 'object' },
  ],
  ProgressSteps: [
    { path: 'content.stepper.items', kind: 'array' },
    { path: 'content.stepper.timer', kind: 'object' },
  ],
  WheelPicker: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.roller.wheels', kind: 'array' },
  ],
  Paywall: [
    { path: 'content.copy.title', kind: 'string' },
    { path: 'content.paywall.products', kind: 'array' },
    { path: 'content.paywall.purchaseAction', kind: 'object' },
  ],
  WebEmbed: [{ path: 'content.embed.html', kind: 'string' }],
  FlexibleLayout: [{ path: 'content.flexibleLayout.sections', kind: 'array' }],
};

const BLOCK_LIBRARY_SIMPLIFIED_CONTENT_KEYS = new Set([
  'copy',
  'header',
  'actionBar',
  'options',
  'carousel',
  'stepper',
  'textField',
  'roller',
  'embed',
  'paywall',
  'flexibleLayout',
]);

/**
 * Small runtime guard for the prompt-facing Simplified V2 screen contract.
 * TypeScript interfaces disappear at runtime; this check prevents an opaque
 * `aiContent` object or an empty manual projection from becoming a persisted
 * library blueprint.
 */
export function validateBlockLibrarySimplifiedScreen(
  value: unknown,
): BlockLibrarySimplifiedScreenValidationIssue[] {
  const issues: BlockLibrarySimplifiedScreenValidationIssue[] = [];
  if (!isRecord(value)) return [{ path: '', message: 'must be an object.' }];
  if (!isNonEmptyString(value.id)) issues.push({ path: 'id', message: 'must be a non-empty string.' });
  if (!isNonEmptyString(value.screenType)
    || !Object.prototype.hasOwnProperty.call(BLOCK_LIBRARY_SCREEN_REQUIRED_FIELDS, value.screenType)) {
    issues.push({ path: 'screenType', message: 'must be a supported Simplified V2 screen type.' });
    return issues;
  }
  if (!isRecord(value.content)) {
    issues.push({ path: 'content', message: 'must be an object.' });
    return issues;
  }
  const unknownContentKeys = Object.keys(value.content)
    .filter((key) => !BLOCK_LIBRARY_SIMPLIFIED_CONTENT_KEYS.has(key));
  if (unknownContentKeys.length > 0) {
    issues.push({
      path: 'content',
      message: `contains unsupported fields: ${unknownContentKeys.join(', ')}.`,
    });
  }
  for (const requirement of BLOCK_LIBRARY_SCREEN_REQUIRED_FIELDS[value.screenType]) {
    const candidate = readNestedValue(value, requirement.path);
    const valid = requirement.kind === 'string'
      ? isNonEmptyString(candidate)
      : requirement.kind === 'array'
        ? Array.isArray(candidate) && candidate.length > 0
        : isRecord(candidate);
    if (!valid) {
      issues.push({
        path: requirement.path,
        message: `must be a non-empty ${requirement.kind}.`,
      });
    }
  }
  return issues;
}

function readNestedValue(value: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, part) => (
    isRecord(current) ? current[part] : undefined
  ), value);
}

function validateAssetExampleSlots(
  blueprint: BlockLibraryBlueprint,
  previewScreens: StrategyStepNode[],
  errors: string[],
): void {
  const blueprintById = new Map(blueprint.screens.map((screen) => [screen.id, screen]));
  const previewById = new Map(previewScreens.map((screen) => [String(screen.id), screen]));
  blueprint.assetExamples.forEach((example, index) => {
    const screen = blueprintById.get(example.screenId);
    const preview = previewById.get(example.screenId) as unknown as Record<string, unknown> | undefined;
    const intentSlots = new Set<string>();
    for (const intent of screen?.aiMeta?.assetIntents ?? []) {
      if (isRecord(intent) && isNonEmptyString(intent.slotRole)) {
        intentSlots.add(String(intent.slotRole));
      }
    }
    const previewSlots = new Set(
      preview && isRecord(preview.aiAssets) ? Object.keys(preview.aiAssets) : [],
    );
    if (!intentSlots.has(example.slotKey) && !previewSlots.has(example.slotKey)) {
      errors.push(
        `blueprint.assetExamples[${index}].slotKey: ${example.screenId}/${example.slotKey} has no matching semantic asset intent or preview asset slot.`,
      );
    }
  });
}

function validatePreviewScreens(value: unknown, errors: string[]): StrategyStepNode[] | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push('previewScreens: at least one full preview screen is required.');
    return undefined;
  }
  const invalid = value.findIndex((screen) => !isRecord(screen) || !isNonEmptyString(screen.id));
  if (invalid >= 0) {
    errors.push(`previewScreens: previewScreens[${invalid}] must be a screen with a stable id.`);
    return undefined;
  }
  return value as unknown as StrategyStepNode[];
}

function validateRepresentationParity(
  blueprint: BlockLibraryBlueprint,
  previewScreens: StrategyStepNode[],
  errors: string[],
): void {
  const blueprintIds = blueprint.screens.map((screen) => String(screen?.id ?? ''));
  const previewIds = previewScreens.map((screen) => String(screen?.id ?? ''));
  if (blueprintIds.some((id) => !id)) errors.push('blueprint.screens: every screen must have a stable id.');
  const duplicates = blueprintIds.filter((id, index) => blueprintIds.indexOf(id) !== index);
  if (duplicates.length > 0) errors.push(`screen_ids_duplicate: ${Array.from(new Set(duplicates)).join(', ')}.`);
  if (canonicalizeBlockLibraryValue(blueprintIds) !== canonicalizeBlockLibraryValue(previewIds)) {
    errors.push('screen_ids_mismatch: blueprint.screens and previewScreens must share ordered ids.');
  }
  const firstScreenId = blueprint.strategicBlock.firstScreenId;
  if (!isNonEmptyString(firstScreenId) || !blueprintIds.includes(firstScreenId)) {
    errors.push('blueprint.strategicBlock.firstScreenId: must identify a blueprint screen.');
  }
  blueprint.screens.forEach((screen, index) => {
    if (previewScreens[index] && String(screen.screenType) !== String(previewScreens[index].screenType)) {
      errors.push(`screen_type_mismatch: screen ${screen.id} differs between blueprint and preview.`);
    }
  });
}

const FORBIDDEN_BLUEPRINT_KEYS = new Set([
  'aiAssets',
  'generatedUrl',
  'posterUrl',
  'referenceImages',
  'provider',
  'model',
  'generationModel',
  'generationTimestamp',
  'sourceDataSnapshot',
  'taskId',
  'taskIds',
]);

function collectForbiddenBlueprintPaths(value: unknown): string[] {
  const paths: string[] = [];
  const visit = (node: unknown, path: string) => {
    if (Array.isArray(node)) {
      node.forEach((item, index) => visit(item, `${path}[${index}]`));
      return;
    }
    if (!isRecord(node)) return;
    for (const [key, child] of Object.entries(node)) {
      const childPath = path ? `${path}.${key}` : key;
      if (FORBIDDEN_BLUEPRINT_KEYS.has(key)) paths.push(childPath);
      else visit(child, childPath);
    }
  };
  visit(value, 'blueprint');
  return paths.slice(0, 20);
}

function validateSourceRef(value: unknown, errors: string[]): BlockLibrarySourceRef | undefined {
  if (!isRecord(value)
    || !isNonEmptyString(value.strategyId)
    || !isNonEmptyString(value.blockId)
    || !isNonEmptyString(value.blockEntityId)) {
    errors.push('sourceRef: sourceRef must carry strategyId, blockId and blockEntityId.');
    return undefined;
  }
  return {
    strategyId: value.strategyId,
    blockId: value.blockId,
    blockEntityId: value.blockEntityId,
  };
}

function parseStoredJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return undefined; }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function asVariableArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).filter((entry) => isNonEmptyString(entry.id));
}

function walkObject(node: unknown, visitor: (obj: Record<string, unknown>) => void): void {
  if (Array.isArray(node)) {
    for (const item of node) walkObject(item, visitor);
    return;
  }
  if (!isRecord(node)) return;
  visitor(node);
  for (const value of Object.values(node)) walkObject(value, visitor);
}
